/* ==========================================================================
   Vireo product landing — behaviour
   ~5 KB, no dependencies, no framework.

   Every interactive part of the design is a custom element, so sections
   re-initialise themselves automatically when the Theme Editor swaps them in
   and out. Nothing here is required for the page to function: the buy box is
   a real <form> that posts to /cart/add, so add-to-cart works with JS off.
   ========================================================================== */
(function () {
  'use strict';

  var BUS = document;
  var SELECTION_EVENT = 'vireo:selection';
  var money = function (el) {
    return el ? el.getAttribute('data-price-html') || '' : '';
  };

  /* ------------------------------------------------------------------ */
  /* Buy box: variant picker + bundle selector + add to cart            */
  /* ------------------------------------------------------------------ */
  class VireoBuyBox extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-vr-variant-id]');
      this.qtyInput = this.querySelector('[data-vr-quantity]');
      this.errorEl = this.querySelector('[data-vr-error]');
      this.submitBtn = this.querySelector('[data-vr-submit]');
      this.bundles = Array.prototype.slice.call(this.querySelectorAll('[data-vr-bundle]'));
      this.variantInputs = Array.prototype.slice.call(this.querySelectorAll('[data-vr-variant-option]'));
      this.atcPriceEls = Array.prototype.slice.call(this.querySelectorAll('[data-vr-atc-price]'));
      this.atcCompareEls = Array.prototype.slice.call(this.querySelectorAll('[data-vr-atc-compare]'));
      this.priceCurrent = this.querySelector('[data-vr-price-current]');
      this.priceCompare = this.querySelector('[data-vr-price-compare]');
      this.cartAction = this.getAttribute('data-cart-action') || 'add';
      this.baseVariantId = this.idInput ? this.idInput.value : null;

      this.onBundleClick = this.onBundleClick.bind(this);
      this.onBundleKey = this.onBundleKey.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onExternalSubmit = this.onExternalSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.bundles.forEach(function (b) {
        b.addEventListener('click', this.onBundleClick);
        b.addEventListener('keydown', this.onBundleKey);
      }, this);

      this.variantInputs.forEach(function (input) {
        input.addEventListener('change', this.onVariantChange);
      }, this);

      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      BUS.addEventListener('vireo:submit', this.onExternalSubmit);

      var initial = this.querySelector('[data-vr-bundle][aria-checked="true"]') || this.bundles[0];
      if (initial) this.select(initial);
      else this.publish();
    }

    disconnectedCallback() {
      BUS.removeEventListener('vireo:submit', this.onExternalSubmit);
    }

    onBundleClick(event) {
      this.select(event.currentTarget);
    }

    onBundleKey(event) {
      var keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
      if (keys.indexOf(event.key) === -1) return;
      event.preventDefault();
      var i = this.bundles.indexOf(event.currentTarget);
      var next = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? i + 1 : i - 1;
      if (next < 0) next = this.bundles.length - 1;
      if (next >= this.bundles.length) next = 0;
      this.bundles[next].focus();
      this.select(this.bundles[next]);
    }

    /* A bundle only overrides the variant when it points at its own SKU;
       otherwise the variant picker stays in charge and the bundle sets quantity. */
    select(bundle) {
      this.bundles.forEach(function (b) {
        b.setAttribute('aria-checked', String(b === bundle));
      });

      var variantId = bundle.getAttribute('data-variant-id') || this.baseVariantId;
      var quantity = bundle.getAttribute('data-quantity') || '1';

      if (variantId && this.idInput) this.idInput.value = variantId;
      if (this.qtyInput) this.qtyInput.value = quantity;

      this.selected = bundle;
      this.publish();
    }

    onVariantChange(event) {
      var input = event.currentTarget;
      this.baseVariantId = input.getAttribute('data-variant-id');

      if (this.priceCurrent) this.priceCurrent.textContent = input.getAttribute('data-price-html') || '';
      if (this.priceCompare) {
        var compare = input.getAttribute('data-compare-html');
        this.priceCompare.innerHTML = compare ? '<s>' + compare + '</s>' : '';
        this.priceCompare.hidden = !compare;
      }

      // Re-apply the active bundle so quantity and the id stay consistent.
      if (this.selected) this.select(this.selected);
      else {
        if (this.idInput) this.idInput.value = this.baseVariantId;
        this.publish();
      }
    }

    /* Broadcast the current selection so the sticky bar and any secondary
       CTA can mirror it without duplicating pricing logic. */
    publish() {
      var source = this.selected || this;
      var priceHtml = money(source) || money(this);
      var compareHtml = source.getAttribute('data-compare-html') || this.getAttribute('data-compare-html') || '';

      // The button carries the same price string as the selected bundle,
      // exactly as in the approved design.
      if (priceHtml) {
        this.atcPriceEls.forEach(function (el) {
          el.innerHTML = priceHtml;
        });
      }

      this.atcCompareEls.forEach(function (el) {
        el.innerHTML = compareHtml ? '<s>' + compareHtml + '</s>' : '';
        el.hidden = !compareHtml;
      });

      BUS.dispatchEvent(
        new CustomEvent(SELECTION_EVENT, {
          detail: {
            sectionId: this.getAttribute('data-section-id'),
            priceHtml: priceHtml,
            available: this.submitBtn ? !this.submitBtn.disabled : true
          }
        })
      );
    }

    onExternalSubmit() {
      if (this.form) this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
    }

    onSubmit(event) {
      // No fetch support → let the browser post the form normally.
      if (typeof window.fetch !== 'function') return;
      event.preventDefault();
      if (this.submitBtn && this.submitBtn.getAttribute('aria-busy') === 'true') return;

      this.setError('');
      this.setBusy(true);

      var body = new FormData(this.form);
      body.append('sections', 'cart-drawer,cart-icon-bubble,cart-notification-product');
      body.append('sections_url', window.location.pathname);

      var self = this;
      // Vacía el carrito antes de agregar: solo queda lo elegido en este momento.
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .catch(function () {})
        .then(function () {
          return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: body
          });
        })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          if (data.status) {
            self.setError(data.description || data.message || '');
            self.setBusy(false);
            return;
          }
          // Regalos del Kit O'Clock: se agregan aparte y quedan gratis por el descuento automático.
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                { id: 50519544234219, quantity: 1 },
                { id: 50519544266987, quantity: 1 },
                { id: 50519544299755, quantity: 1 }
              ]
            })
          })
            .catch(function () {})
            .then(function () {
              if (self.cartAction === 'checkout') {
                window.location.href = '/checkout';
                return;
              }
              self.setBusy(false);
              self.openCart(data);
            });
        })
        .catch(function () {
          // Network hiccup — fall back to the plain, always-works form post.
          self.form.submit();
        });
    }

    /* Hand off to whatever cart UI the host theme provides, in the order
       Dawn-derived themes (Shrine included) expose it. */
    openCart(data) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        return;
      }
      var notification = document.querySelector('cart-notification');
      if (notification && typeof notification.renderContents === 'function') {
        notification.renderContents(data);
        return;
      }
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }

    setBusy(state) {
      if (!this.submitBtn) return;
      this.submitBtn.setAttribute('aria-busy', String(state));
      this.submitBtn.disabled = state;
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.setAttribute('data-visible', String(Boolean(message)));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Gallery: thumbnail → main image                                     */
  /* ------------------------------------------------------------------ */
    class VireoGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-vr-gallery-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-vr-gallery-slide]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-vr-gallery-thumb]'));
      this.thumbsRail = this.querySelector('.vr-gallery__thumbs');
      this.prevBtn = this.querySelector('[data-vr-gallery-prev]');
      this.nextBtn = this.querySelector('[data-vr-gallery-next]');
      if (!this.track || this.slides.length === 0) return;

      this.activeIndex = 0;
      this.dragging = false;
      this.dragStartX = 0;
      this.dragDelta = 0;

      this.thumbDragging = false;
      this.thumbDragStartX = 0;
      this.thumbDragStartScroll = 0;
      this.thumbDragMoved = false;
      this.suppressThumbClick = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onThumbPointerDown = this.onThumbPointerDown.bind(this);
      this.onThumbPointerMove = this.onThumbPointerMove.bind(this);
      this.onThumbPointerUp = this.onThumbPointerUp.bind(this);

      if (this.slides.length > 1) {
        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('pointermove', this.onPointerMove);
        this.track.addEventListener('pointerup', this.onPointerUp);
        this.track.addEventListener('pointercancel', this.onPointerUp);
      }

      if (this.thumbsRail) {
        this.thumbsRail.addEventListener('pointerdown', this.onThumbPointerDown);
        this.thumbsRail.addEventListener('pointermove', this.onThumbPointerMove);
        this.thumbsRail.addEventListener('pointerup', this.onThumbPointerUp);
        this.thumbsRail.addEventListener('pointercancel', this.onThumbPointerUp);
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          this.go(this.activeIndex - 1);
        }.bind(this));
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          this.go(this.activeIndex + 1);
        }.bind(this));
      }

      this.thumbs.forEach(function (thumb, index) {
        thumb.addEventListener('click', function (event) {
          if (this.suppressThumbClick) {
            event.preventDefault();
            this.suppressThumbClick = false;
            return;
          }
          this.go(index);
        }.bind(this));
      }, this);
    }

    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('pointermove', this.onPointerMove);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
      }
      if (this.thumbsRail) {
        this.thumbsRail.removeEventListener('pointerdown', this.onThumbPointerDown);
        this.thumbsRail.removeEventListener('pointermove', this.onThumbPointerMove);
        this.thumbsRail.removeEventListener('pointerup', this.onThumbPointerUp);
        this.thumbsRail.removeEventListener('pointercancel', this.onThumbPointerUp);
      }
    }

    // Finger swipe only — desktop keeps mouse dragging off and uses the arrows.
    onPointerDown(event) {
      if (event.pointerType !== 'touch') return;
      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragDelta = 0;
      this.track.style.transition = 'none';
      this.track.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      this.dragDelta = event.clientX - this.dragStartX;
      this.track.style.transform = 'translateX(calc(-' + this.activeIndex * 100 + '% + ' + this.dragDelta + 'px))';
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.style.transition = '';
      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch (e) {}

      var threshold = this.track.clientWidth * 0.15;
      var next = this.activeIndex;
      if (this.dragDelta < -threshold) next = this.activeIndex + 1;
      else if (this.dragDelta > threshold) next = this.activeIndex - 1;
      this.dragDelta = 0;
      this.go(next);
    }

    // Thumbnail rail: mouse drag-to-scroll. Touch already scrolls natively
    // since the rail is a plain overflow-x:auto container.
    onThumbPointerDown(event) {
      if (event.pointerType !== 'mouse') return;
      this.thumbDragging = true;
      this.thumbDragMoved = false;
      this.thumbDragStartX = event.clientX;
      this.thumbDragStartScroll = this.thumbsRail.scrollLeft;
      this.thumbsRail.setPointerCapture(event.pointerId);
      this.thumbsRail.setAttribute('data-dragging', 'true');
    }

    onThumbPointerMove(event) {
      if (!this.thumbDragging) return;
      var delta = event.clientX - this.thumbDragStartX;
      if (Math.abs(delta) > 4) this.thumbDragMoved = true;
      this.thumbsRail.scrollLeft = this.thumbDragStartScroll - delta;
    }

    onThumbPointerUp(event) {
      if (!this.thumbDragging) return;
      this.thumbDragging = false;
      this.thumbsRail.removeAttribute('data-dragging');
      if (this.thumbDragMoved) this.suppressThumbClick = true;
      try {
        this.thumbsRail.releasePointerCapture(event.pointerId);
      } catch (e) {}
    }

    // Wraps at both ends: past the last image goes to the first, and back
    // past the first goes to the last.
    go(index) {
      var count = this.slides.length;
      var next = ((index % count) + count) % count;
      this.activeIndex = next;
      this.track.style.transform = 'translateX(-' + next * 100 + '%)';
      this.thumbs.forEach(function (thumb, i) {
        thumb.setAttribute('aria-current', String(i === next));
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Ingredients: thumbnail rail → detail card                           */
  /* ------------------------------------------------------------------ */
  class VireoIngredients extends HTMLElement {
    connectedCallback() {
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-vr-ingredient-panel]'));
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-vr-ingredient-tab]'));
      this.prevBtn = this.querySelector('[data-vr-ingredient-prev]');
      this.nextBtn = this.querySelector('[data-vr-ingredient-next]');
      this.activeIndex = 0;

      var self = this;
      this.tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          self.show(index);
        });
        tab.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          var direction = event.key === 'ArrowRight' ? 'next' : 'prev';
          var next = direction === 'next' ? index + 1 : index - 1;
          if (next < 0) next = self.tabs.length - 1;
          if (next >= self.tabs.length) next = 0;
          self.tabs[next].focus();
          self.show(next, direction);
        });
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          self.step('prev');
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          self.step('next');
        });
      }

      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onResize = this.onResize.bind(this);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.positionArrows();
    }

    disconnectedCallback() {
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('resize', this.onResize);
    }

    step(direction) {
      var next = direction === 'next' ? this.activeIndex + 1 : this.activeIndex - 1;
      if (next < 0) next = this.tabs.length - 1;
      if (next >= this.tabs.length) next = 0;
      this.show(next, direction);
    }

    // Swipe left/right anywhere on the section to move to the next/previous ingredient.
    onTouchStart(event) {
      this.swipeStartX = event.touches[0].clientX;
    }

    onTouchEnd(event) {
      if (this.swipeStartX == null) return;
      var deltaX = event.changedTouches[0].clientX - this.swipeStartX;
      this.swipeStartX = null;
      if (Math.abs(deltaX) < 40) return;
      this.step(deltaX < 0 ? 'next' : 'prev');
    }

    onResize() {
      if (this.resizeRaf) return;
      var self = this;
      this.resizeRaf = requestAnimationFrame(function () {
        self.resizeRaf = null;
        self.positionArrows();
      });
    }

    // Keeps the arrows vertically centred on the active panel's full height.
    positionArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      var panel = this.panels[this.activeIndex];
      if (!panel) return;
      var top = panel.getBoundingClientRect().height / 2;
      if (this.prevBtn) this.prevBtn.style.top = top + 'px';
      if (this.nextBtn) this.nextBtn.style.top = top + 'px';
    }

    show(index, direction) {
      if (!direction) {
        direction = index > this.activeIndex ? 'next' : 'prev';
      }
      this.activeIndex = index;

      this.panels.forEach(function (panel, i) {
        panel.classList.remove('vr-ingredient--enter-next', 'vr-ingredient--enter-prev');
        if (i === index) {
          panel.classList.add(direction === 'next' ? 'vr-ingredient--enter-next' : 'vr-ingredient--enter-prev');
        }
        panel.hidden = i !== index;
      });
      this.tabs.forEach(function (tab, i) {
        tab.setAttribute('aria-selected', String(i === index));
        tab.setAttribute('tabindex', i === index ? '0' : '-1');
      });

      this.positionArrows();
    }
  }

  /* ------------------------------------------------------------------ */
  /* FAQ accordion                                                       */
  /* ------------------------------------------------------------------ */
  class VireoFaq extends HTMLElement {
    connectedCallback() {
      this.single = this.getAttribute('data-single-open') === 'true';
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-vr-faq-toggle]'));
      var self = this;
      this.buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          self.toggle(button);
        });
      });
    }

    toggle(button) {
      var open = button.getAttribute('aria-expanded') === 'true';
      if (this.single && !open) {
        this.buttons.forEach(function (other) {
          other.setAttribute('aria-expanded', 'false');
          var panel = document.getElementById(other.getAttribute('aria-controls'));
          if (panel) panel.hidden = true;
        });
      }
      button.setAttribute('aria-expanded', String(!open));
      var target = document.getElementById(button.getAttribute('aria-controls'));
      if (target) target.hidden = open;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sticky bar: mirrors the buy box, triggers its submit                */
  /* ------------------------------------------------------------------ */
  class VireoSticky extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-vr-sticky-price]');
      this.button = this.querySelector('[data-vr-sticky-button]');
      this.bar = this.querySelector('.vr-sticky');

      this.onSelection = this.onSelection.bind(this);
      BUS.addEventListener(SELECTION_EVENT, this.onSelection);

      var self = this;
      if (this.button) {
        this.button.addEventListener('click', function () {
          BUS.dispatchEvent(new CustomEvent('vireo:submit'));
        });
      }

      if (this.bar && this.bar.getAttribute('data-reveal') === 'on-scroll') this.watch();
    }

    disconnectedCallback() {
      BUS.removeEventListener(SELECTION_EVENT, this.onSelection);
      if (this.observer) this.observer.disconnect();
    }

    /* Reveal once the primary add-to-cart has scrolled out of view. */
    watch() {
      var anchor = document.querySelector('[data-vr-sticky-anchor]');
      if (!anchor || typeof IntersectionObserver !== 'function') {
        this.bar.setAttribute('data-visible', 'true');
        return;
      }
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.bar.setAttribute('data-visible', String(!entry.isIntersecting));
          });
        },
        { rootMargin: '0px 0px -20% 0px' }
      );
      this.observer.observe(anchor);
    }

    onSelection(event) {
      if (this.priceEl && event.detail.priceHtml) this.priceEl.innerHTML = event.detail.priceHtml;
      if (this.button && typeof event.detail.available === 'boolean') {
        this.button.disabled = !event.detail.available;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Secondary CTA (final call-to-action) — reuses the buy box's form    */
  /* ------------------------------------------------------------------ */
  class VireoCtaTrigger extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-vr-cta-price]');
      this.button = this.querySelector('button');
      this.onSelection = this.onSelection.bind(this);
      BUS.addEventListener(SELECTION_EVENT, this.onSelection);

      if (this.button) {
        this.button.addEventListener('click', function () {
          var buybox = document.querySelector('vireo-buy-box');
          if (buybox) {
            BUS.dispatchEvent(new CustomEvent('vireo:submit'));
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        });
      }
    }

    disconnectedCallback() {
      BUS.removeEventListener(SELECTION_EVENT, this.onSelection);
    }

    onSelection(event) {
      if (this.priceEl && event.detail.priceHtml) this.priceEl.innerHTML = event.detail.priceHtml;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Photo reviews: draggable carousel, arrows + dots                    */
  /* ------------------------------------------------------------------ */
  class VireoPhotoReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-vr-photoreviews-track]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-vr-photoreviews-card]'));
      this.dots = Array.prototype.slice.call(this.parentElement.querySelectorAll('[data-vr-photoreviews-dot]'));
      this.prevBtn = this.querySelector('[data-vr-photoreviews-prev]');
      this.nextBtn = this.querySelector('[data-vr-photoreviews-next]');
      if (!this.track || this.cards.length === 0) return;

      this.dragging = false;
      this.dragStartX = 0;
      this.dragStartScroll = 0;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          this.step(-1);
        }.bind(this));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          this.step(1);
        }.bind(this));
      }

      this.dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          this.track.scrollTo({ left: this.cards[index].offsetLeft, behavior: 'smooth' });
        }.bind(this));
      }, this);

      this.onScroll();
      this.positionArrows();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    // Mouse drag-to-scroll. Touch already scrolls natively.
    onPointerDown(event) {
      if (event.pointerType !== 'mouse') return;
      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.track.setAttribute('data-dragging', 'true');
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      var delta = event.clientX - this.dragStartX;
      this.track.scrollLeft = this.dragStartScroll - delta;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.removeAttribute('data-dragging');
      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch (e) {}
    }

    step(direction) {
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      var amount = (this.cards[0].getBoundingClientRect().width + gap) * direction;
      this.track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    onScroll() {
      if (this.scrollRaf) return;
      var self = this;
      this.scrollRaf = requestAnimationFrame(function () {
        self.scrollRaf = null;
        var scrollLeft = self.track.scrollLeft;
        var maxScroll = self.track.scrollWidth - self.track.clientWidth;

        if (self.prevBtn) self.prevBtn.disabled = scrollLeft <= 2;
        if (self.nextBtn) self.nextBtn.disabled = scrollLeft >= maxScroll - 2;

        var closest = 0;
        var closestDist = Infinity;
        self.cards.forEach(function (card, i) {
          var dist = Math.abs(card.offsetLeft - scrollLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        self.dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', String(i === closest));
        });
      });
    }

    // Keeps the arrows vertically centred on the photo band, whose height
    // now depends on the card's (responsive) width via aspect-ratio.
    onResize() {
      if (this.resizeRaf) return;
      var self = this;
      this.resizeRaf = requestAnimationFrame(function () {
        self.resizeRaf = null;
        self.positionArrows();
      });
    }

    positionArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      var media = this.querySelector('.vr-photoreviews__media');
      if (!media) return;
      var top = media.getBoundingClientRect().height / 2;
      if (this.prevBtn) this.prevBtn.style.top = top + 'px';
      if (this.nextBtn) this.nextBtn.style.top = top + 'px';
    }
  }

  class VireoTransformation extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-vr-transformation-track]');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-vr-transformation-card]'));
      this.dots = Array.prototype.slice.call(this.parentElement.querySelectorAll('[data-vr-transformation-dot]'));
      this.prevBtn = this.querySelector('[data-vr-transformation-prev]');
      this.nextBtn = this.querySelector('[data-vr-transformation-next]');
      if (!this.track || this.cards.length === 0) return;

      this.dragging = false;
      this.dragStartX = 0;
      this.dragStartScroll = 0;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          this.step(-1);
        }.bind(this));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          this.step(1);
        }.bind(this));
      }

      this.dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          this.track.scrollTo({ left: this.cards[index].offsetLeft, behavior: 'smooth' });
        }.bind(this));
      }, this);

      this.onScroll();
      this.positionArrows();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse') return;
      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
      this.track.setAttribute('data-dragging', 'true');
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      var delta = event.clientX - this.dragStartX;
      this.track.scrollLeft = this.dragStartScroll - delta;
    }

    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.track.removeAttribute('data-dragging');
      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch (e) {}
    }

    step(direction) {
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      var amount = (this.cards[0].getBoundingClientRect().width + gap) * direction;
      this.track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    onScroll() {
      if (this.scrollRaf) return;
      var self = this;
      this.scrollRaf = requestAnimationFrame(function () {
        self.scrollRaf = null;
        var scrollLeft = self.track.scrollLeft;
        var maxScroll = self.track.scrollWidth - self.track.clientWidth;

        if (self.prevBtn) self.prevBtn.disabled = scrollLeft <= 2;
        if (self.nextBtn) self.nextBtn.disabled = scrollLeft >= maxScroll - 2;

        var closest = 0;
        var closestDist = Infinity;
        self.cards.forEach(function (card, i) {
          var dist = Math.abs(card.offsetLeft - scrollLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        self.dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', String(i === closest));
        });
      });
    }

    onResize() {
      if (this.resizeRaf) return;
      var self = this;
      this.resizeRaf = requestAnimationFrame(function () {
        self.resizeRaf = null;
        self.positionArrows();
      });
    }

    positionArrows() {
      if (!this.prevBtn && !this.nextBtn) return;
      var card = this.cards[0];
      if (!card) return;
      var top = card.getBoundingClientRect().height / 2;
      if (this.prevBtn) this.prevBtn.style.top = top + 'px';
      if (this.nextBtn) this.nextBtn.style.top = top + 'px';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Bundle kit: expande/colapsa el checklist + Kit Youpiter según la    */
  /* opción de compra elegida. No toca VireoBuyBox — escucha los mismos  */
  /* botones de forma independiente, solo para esta capa visual.         */
  /* ------------------------------------------------------------------ */
  class VireoBundleKit extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-vr-bundle]'));
      this.wraps = Array.prototype.slice.call(this.querySelectorAll('.vr-bundle-wrap'));
      if (this.buttons.length === 0) return;

      var self = this;
      this.buttons.forEach(function (button, index) {
        button.addEventListener('click', function () {
          self.expand(index);
        });
      });

      var initialIndex = 0;
      this.buttons.forEach(function (button, index) {
        if (button.getAttribute('aria-checked') === 'true') initialIndex = index;
      });
      this.expand(initialIndex);
    }

    expand(index) {
      this.wraps.forEach(function (wrap, i) {
        var panel = wrap.querySelector('[data-vr-bundle-kit-panel]');
        if (!panel) return;
        panel.hidden = i !== index;
      });
    }
  }

  var registry = {
    'vireo-buy-box': VireoBuyBox,
    'vireo-gallery': VireoGallery,
    'vireo-ingredients': VireoIngredients,
    'vireo-faq': VireoFaq,
    'vireo-sticky': VireoSticky,
    'vireo-cta-trigger': VireoCtaTrigger,
    'vireo-photo-reviews': VireoPhotoReviews,
    'vireo-transformation': VireoTransformation,
    'vireo-bundle-kit': VireoBundleKit
  };
  Object.keys(registry).forEach(function (tag) {
    if (!customElements.get(tag)) customElements.define(tag, registry[tag]);
  });
})();
